import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AlertTriangle, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";

/**
 * Two-step delete:
 *  1. Default — remove the user from the ACTIVE client only (their account
 *     and access to other clients are untouched; account auto-deactivates
 *     when its last client membership is removed).
 *  2. "Permanent delete" toggle — only shown if caller has `delete_user_permanent`
 *     (typically super admin). Deletes the account across ALL clients.
 */
export default function UserDeleteDialog({ open, onOpenChange, user: target, onDeleted }) {
  const { hasPerm } = useAuth();
  const { active } = useClient();
  const clientName = active?.name || "this client";
  const canPermanent = hasPerm("delete_user_permanent");
  const [mode, setMode] = useState("soft");
  const [typed, setTyped] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setMode("soft");
      setTyped("");
    }
  }, [open]);

  if (!target) return null;

  const canConfirm = mode === "soft" || typed.trim().toLowerCase() === target.email.toLowerCase();

  const submit = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      const url = mode === "hard" ? `/users/${target.id}?hard=true` : `/users/${target.id}`;
      await api.delete(url);
      toast.success(mode === "hard" ? "User permanently deleted" : `${target.name} removed from ${clientName}`);
      onOpenChange(false);
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="delete-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-500" />
            {mode === "hard" ? "Permanently delete user" : `Remove user from ${clientName}`}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {mode === "soft"
              ? `Removes this user's access to ${clientName} only. Their account and access to any other clients stay intact. If this was their last client, the account is deactivated.`
              : "Permanent delete removes the user account across ALL clients. Audit-log and ticket history will still reference their email, but the user row is gone forever."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 text-sm">
          <div className="bg-slate-50 border border-slate-200 rounded p-3">
            <div className="text-xs text-slate-500">Target</div>
            <div className="font-medium text-slate-800">{target.name}</div>
            <div className="text-xs text-slate-600 font-mono">{target.email}</div>
          </div>

          {mode === "hard" && (
            <div className="space-y-1.5">
              <Label className="text-xs text-rose-700">
                Type <span className="font-mono font-semibold">{target.email}</span> to confirm
              </Label>
              <Input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={target.email}
                className="font-mono text-xs"
                data-testid="delete-confirm-input"
                autoFocus
              />
            </div>
          )}

          {canPermanent && (
            <button
              type="button"
              onClick={() => { setMode(mode === "soft" ? "hard" : "soft"); setTyped(""); }}
              className="text-xs text-rose-600 hover:text-rose-700 underline"
              data-testid="toggle-permanent-delete"
            >
              {mode === "soft" ? "I need to permanently delete instead →" : "← Back to soft deactivate"}
            </button>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={!canConfirm || submitting}
            className={mode === "hard" ? "bg-rose-600 hover:bg-rose-700" : "bg-slate-700 hover:bg-slate-800"}
            data-testid="delete-confirm-btn"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            {submitting ? "Working..." : (mode === "hard" ? "Permanently delete" : "Remove from client")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
